import IChatAppeal from "@/interfaces/chatAppeal";
import gql from "@/repo/apolloClient";
import { showNotification } from "@/utility";
import { makeAutoObservable, runInAction } from "mobx";
import Logo from "@/assets/icons/P2P_Logo.svg";

interface State {
  appealMessages?: IChatAppeal[] | null;
  myAppealMessages?: IChatAppeal[] | null;
}
export class ChatAppealStore {
  private state: State = {
    appealMessages: undefined,
    myAppealMessages: undefined,
  };
  constructor() {
    makeAutoObservable(this);
  }
  public get appeals() {
    return this.state.appealMessages;
  }

  public sendMessage = async (
    id: string,
    content: string,
    participants: string[],
    message_type: "text" | "image" | "status",
  ) => {
    const query = `
      mutation sendMessage($participants: String!,$appeal_id: uuid!,$message_type: String!) {
        insert_appeal_chat_messages_one(object: { participants: $participants ,content: "${content}", appeal_id: $appeal_id, message_type: $message_type }) {
          message_id
        }
      }
    `;
    await gql.Mutation(query, {
      participants: JSON.stringify(participants)
        .replace("[", "")
        .replace("]", "")
        .replaceAll('"', ""),
      appeal_id: id,
      message_type,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleMyAppealMessagesUpdate = (response: any) => {
    const messages = response.data.chat_order_messages ?? null;

    runInAction(() => {
      this.state.myAppealMessages = messages;
      if (messages) {
        const latestMessageTimestamp = localStorage.getItem(
          "latestAppealMessageTimestamp",
        );

        const latestTimestamp = latestMessageTimestamp
          ? Number(latestMessageTimestamp)
          : null;
        const currentURL = window.location.hash;
        let notifyCount = 0;

        for (const message of messages) {
          const messageTimestamp = new Date(message.created_at).getTime();
          const orderPageURL = "#/trade/orders/" + message.order_id;

          if (notifyCount === 5) break;

          if (
            (!latestTimestamp || messageTimestamp > latestTimestamp) &&
            orderPageURL !== currentURL
          ) {
            notifyCount += 1;
            showNotification({
              title: "New appeal's message!",
              body: "You have received a new message. Click to go to appeal chat.",
              icon: Logo,
              path: "/" + orderPageURL,
            });
          }
        }

        const latestMessage = messages[messages.length - 1];
        if (latestMessage) {
          const latestMessageTimestamp = new Date(latestMessage.created_at)
            .getTime()
            .toString();
          localStorage.setItem(
            "latestAppealMessageTimestamp",
            latestMessageTimestamp,
          );
        }
      }
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAppealMessagesUpdate = (response: any) => {
    runInAction(() => {
      this.state.appealMessages = response.data.appeal_chat_messages ?? null;
    });
  };

  public subscribeToMyAppealMessages = async (user_id: string) => {
    const query = `
    subscription GetLatestMessages {
      appeal_chat_messages(
        where: { created_by: { _neq: "${user_id}" } }
        order_by: { created_at: asc }
      ) {
        message_id
        message_type
        content
        created_at
        appeal_id
        created_by
        participants
      }
    }
    `;
    const subscription = (await gql.Subscribe(query)).subscribe({
      next: this.handleMyAppealMessagesUpdate,
      error: (error) =>
        console.error("Error fetching my appeal messages: ", error),
    });

    return () => subscription.unsubscribe();
  };
  public subscribeToAppealMessages = async (appeal_id: string) => {
    const query = `
    subscription OnChatMessage {
      appeal_chat_messages(where: { appeal_id: { _eq: "${appeal_id}" } }) {
        content
        message_id
        appeal_id
        message_type
        created_at
        created_by
        participants
      }
    }
  `;
    const subscription = (await gql.Subscribe(query)).subscribe({
      next: this.handleAppealMessagesUpdate,
      error: (error) =>
        console.error("Error fetching appeal messages: ", error),
    });

    return () => subscription.unsubscribe();
  };

  public get myAppealMessages() {
    return this.state.myAppealMessages;
  }
  public get appealMessages() {
    return this.state.appealMessages;
  }
}
